import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Row, UncontrolledDropdown } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb"
import { MetaTags } from "react-meta-tags";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton";
import BootstrapTable from 'react-bootstrap-table-next';
import DeleteModal from 'components/Common/DeleteModal';
import { AppConfiguration } from 'config';

import { useDispatch, useSelector } from "react-redux";
import { ADMIN_STATUS, DEFAULT_TABLE_SORT_BY_CREATED_AT_IN_DESC, MODE, REQUIRED_FEATURE_FLAGS, TABLE_COLMN_NAMES } from "helpers/sf_constants";
import { capitalizeWord, getPrimaryButtonClass, getPrimaryTextClass, forColumnHeaderWrapper } from "helpers/sf_utils";
import AccountsOrUsers from "components/Common/AccountsOrUsers";
import Select from "react-select";

import { getOldAdminUsers } from "store/admin/users/actions";
import { createUserNotifications, deleteUserNotifications, exportAdminUserNotifications, getAdminUserNotifications, updateUserNotifications } from "store/notifications-terminal/actions";
import { USER_NOTIFICATION_DELETE_STR } from "common/sf_strings";
import { dateCreatedFormatter } from "pages/Admin/Users/UserComponents/UserList/Formatters";
import { getAdminInternalUsers } from "store/admin/admin-internal-user/actions";
import { customSortCaret } from "helpers/bootstrap_table_helper";
import OffsetPagination from "components/Common/OffsetPagination";
import {
    NOTIFICATIONS_TERMINAL_DEFAULT_SORT_COLUMN,
    NOTIFICATIONS_TERMINAL_DEFAULT_SORT_DIRECTION,
    applyNotificationsTerminalSortParams,
    buildNotificationsTerminalExportParams,
    isNotificationsTerminalColumnBackendSortable,
} from "helpers/notifications_terminal_sort_helper";
import useFeatureFlags from "hooks/useFeatureFlags";


const optionBulkEdit = [
    {
        options: [
            { label: 'Set Active', value: 'active' },
            { label: 'Set Inactive', value: 'inactive' },
            { label: 'Delete', value: 'delete' },
        ],
    },
];

const HtmlView = ({ content }) => {
    return (
        <div dangerouslySetInnerHTML={{ __html: content }} />
    );
};

const NotificationsTerminal = () => {
    const dispatch = useDispatch();
    const [isFeatureFlagActive] = useFeatureFlags();
    const { banner_messages, banner_messages_loading, banner_messages_error, admin_users, admin_user_notifications_loading, admin_user_notifications, last_page_fetched, total_number_of_pages, admin_manage_employees } = useSelector((state) => ({
        banner_messages_error: state.BannerMessages.error,
        banner_messages: state.BannerMessages.banner_messages,
        banner_messages_loading: state.BannerMessages.loading,
        admin_users: state.AdminUsers.admin_users,
        admin_user_notifications_loading: state.NotificationsTerminal.loading,
        admin_user_notifications: state.NotificationsTerminal.admin_user_notifications,
        last_page_fetched: state.NotificationsTerminal.last_page_fetched,
        total_number_of_pages: state.NotificationsTerminal.total_number_of_pages,
        admin_manage_employees: state.AdminManageInternalUsers.admin_manage_employees,
    }));
    const currentPage = useMemo(() => Math.max(1, Number(last_page_fetched) || 1), [last_page_fetched]);
    const shouldUseOffsetPagination = useMemo(() => (
        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_OFFSET_PAGINATION_APPLICATION)
    ), [isFeatureFlagActive]);
    const [notificationsList, setNotificationsList] = useState([]);
    const [notificationsPageLoading, setNotificationsPageLoading] = useState(false);
    const appendModeRef = useRef(false);

    const backendSortingEnabled = useMemo(() => (
        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_BACKEND_COLUMN_SORTING)
    ), [isFeatureFlagActive]);
    const [sortState, setSortState] = useState({
        column: NOTIFICATIONS_TERMINAL_DEFAULT_SORT_COLUMN,
        direction: NOTIFICATIONS_TERMINAL_DEFAULT_SORT_DIRECTION,
    });
    const isInitialSortRender = useRef(true);

    // Backend sorting only: update sort state; the effect below refetches page 1
    // with the new sort. Ignore the table's mount-time re-fire of the current sort.
    const handleTableChange = (type, { sortField, sortOrder }) => {
        if (type !== 'sort') return;
        if (sortField === sortState.column && sortOrder === sortState.direction) return;
        // Recorded whichever way the grid is sorting. With backend sorting off the
        // table still orders its own rows and no refetch happens (see the effect
        // below), but the export needs to know what the admin is looking at.
        setSortState({ column: sortField, direction: sortOrder });
    };

    useEffect(() => {
        dispatch(getOldAdminUsers());
        setNotificationsPageLoading(true);
        dispatch(getAdminUserNotifications({ queryParams: { page_number: 1 } }, true));
        dispatch(getAdminInternalUsers())
    }, [dispatch])

    const [accountsOrUsersModalState, setAccountsOrUsersModalState] = useState({ active: null });
    const [loadingCell, setLoadingCell] = useState({ row: null, col: null });
    const [editIconRow, setEditIconRow] = useState(); // State variable to show/hide
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkEdit, setbulkEdit] = useState();

    const onClickExport = () => {
        // Async CSV export emailed to the admin (all matching notifications).
        const queryParams = buildNotificationsTerminalExportParams(sortState, backendSortingEnabled);
        dispatch(exportAdminUserNotifications({ queryParams }));
    }

    const onClickEditModalOpen = (row, mode) => {
        setAccountsOrUsersModalState({ active: true, row: row, accounts_and_users: [], mode: mode });
    }

    const onClickOpenModal = () => {
        setAccountsOrUsersModalState({ active: true, message: "", accounts_and_users: admin_users, admin_manage_employees: admin_manage_employees });
    }

    const onUpdateStatus = (row, value) => {
        const data = {
            id: row?.id,
            status: value
        }
        dispatch(updateUserNotifications(data));
    }

    const onClickDeleteUserNotifications = (row) => {
        const data = {
            id: row?.id,
        }
        dispatch(deleteUserNotifications(data));
    }

    const onClickOpenDeleteNotificationModal = (value, row = null) => {
        setDeleteNotificationModalState({ action: value, row: row })
    }

    const onConfirmStatusPrompt = (state, values, ids, product_type) => {
        if (state?.mode === MODE.EDIT) {
            const updates = {
                id: state?.row?.id,
                banner_type: values?.banner_type,
                message: values?.message,
                status: state?.row?.status
            }
            dispatch(updateUserNotifications(updates));
            return;
        }

        const data = {
            ...values,
            ...ids,
            product_type: product_type
        }
        dispatch(createUserNotifications(data));
    }

    function OptionFormatter(cellContent, row) {
        return (
            <div className="d-flex gap-3 justify-content-start">
                <UncontrolledDropdown>
                    <DropdownToggle
                        href="#"
                        className="card-drop"
                        tag="i"
                    >
                        <i className="mdi mdi-dots-horizontal font-size-18" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem header className='text-black'>Options</DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem>
                            <div onClick={() => onClickEditModalOpen(row, MODE.EDIT)}>
                                Edit
                            </div>
                        </DropdownItem>
                        <DropdownItem>
                            <div onClick={() => onClickOpenDeleteNotificationModal(true, row)}>
                                Delete
                            </div>
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        );
    }

    function UserIdFormatter(cellContent, row) {
        if (row?.user_id) {
            return (<span>{row.user_id}</span>)
        } else if (row?.admin_user_id) {
            return (<span>
                {row.admin_user_id}
                <i className={`text-capitalize ${getPrimaryTextClass()} ms-1`} style={{ fontSize: "9px" }}>(Admin)</i>
            </span>)
        } else {
            return (<span>--</span>)
        }
    }
    function UserNameFormatter(cellContent, row) {
        if (row?.user_id) {
            return (<span>{`${row?.user?.first_name || ""} ${row?.user?.last_name || ""}`}</span>)
        } else if (row?.admin_user_id) {
            return (<span>{`${row?.admin_user?.first_name || ""} ${row?.admin_user?.last_name || ""}`}</span>)
        } else {
            return (<span>--</span>)
        }
    }

    const rowEvents = {
        onMouseEnter: (_e, _column, rowIndex) => {
            setEditIconRow(rowIndex);
        },
        onMouseLeave: (_e, _column, _rowIndex) => {
            setEditIconRow(null);
        },
    };

    const columns = [
        {
            dataField: "status",
            text: "Status",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    {
                        setLoadingCell({
                            row: rowIndex,
                            col: TABLE_COLMN_NAMES.STATUS,
                        })
                    }
                },
            },
            formatter: (cellContent, row, rowIndex) => {
                return (
                    <div className="d-flex gap-3 justify-content-start">
                        <UncontrolledDropdown>
                            <DropdownToggle className="card-drop" tag="i">
                                {
                                    <>
                                        <i className={`mdi mdi-circle font-size-16 ${cellContent === ADMIN_STATUS.ACTIVE ? 'sfgreen' : 'sfred'}`} />
                                        {(
                                            <span className={`font-size-16 ${(rowIndex === editIconRow) ? '' : 'edit-hover-class'}`}>
                                                <a><i className="mdi mdi-chevron-down" /></a>
                                            </span>)}
                                    </>
                                }

                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-start">
                                <DropdownItem onClick={() => onUpdateStatus(row, ADMIN_STATUS.ACTIVE)}>
                                    <div className="d-inline-flex align-content-center">
                                        <i className='mdi mdi-circle font-size-16 sfgreen' />
                                        <div className="ms-2 d-flex align-self-center">Active</div>
                                    </div>
                                </DropdownItem>
                                <DropdownItem onClick={() => onUpdateStatus(row, ADMIN_STATUS.INACTIVE)}>
                                    <div className="d-inline-flex align-content-center">
                                        <i className='mdi mdi-circle font-size-16 sfred' />
                                        <div className="ms-2 d-flex align-self-center">Paused</div>
                                    </div>
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                )
            },
            // formatter: statusFormatter
        },
        {
            dataField: "id",
            hidden: true,
            csvExport: false
        },
        {
            dataField: "user_id",
            text: "User ID",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: UserIdFormatter,
            csvFormatter: (_cellContent, row, rowIndex) => (row?.user_id ? row?.user_id : (row?.admin_user_id ? row?.admin_user_id : "--"))
        },
        {
            dataField: "user_name",
            text: "User Name",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: UserNameFormatter,
            csvFormatter: (_cellContent, row, rowIndex) => (row?.user_id ? (`${row.user.first_name} ${row.user.last_name}`) : (row?.admin_user_id ? (`${row.admin_user.first_name} ${row.admin_user.last_name}`) : "--"))
        },
        {
            dataField: "user[accounts][0][id]",
            text: "Account ID",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: (cell) => (<span className="text-break">{cell || "--"}</span>),
            csvFormatter: (cellContent) => (cellContent || "--")
        },
        {
            dataField: "user[accounts][0][company_name]",
            text: "Company Name",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: (cell) => (<span className="text-break">{cell || "--"}</span>),
            csvFormatter: (cellContent) => (cellContent || "--")
        },
        {
            dataField: "banner_type",
            text: "Banner Type",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: (cell, row, rowIndex) => (<span className="text-break">{capitalizeWord(cell)}</span>)
        },
        {
            dataField: "message",
            text: "Message",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: (cell, row, rowIndex) => (<HtmlView content={cell} />)
        },
        {
            dataField: "created_at",
            text: "Created At",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: dateCreatedFormatter,
            csvFormatter: dateCreatedFormatter
        },
        {
            dataField: "",
            text: "Options",
            sort: false,
            csvExport: false,
            formatter: OptionFormatter
        },
    ]

    // When backend sorting is on, only whitelisted DB-native columns are
    // sortable; disable the rest so we never send an unsupported sort_column
    // to the API.
    const tableColumns = backendSortingEnabled
        ? columns.map((column) => ({ ...column, sort: column.sort && isNotificationsTerminalColumnBackendSortable(column.dataField) }))
        : columns

    // Select All Button operation
    const selectRow = {
        mode: 'checkbox',
        selected: selectedRows.map((row) => row.id),
        onSelectAll: (isSelect, rows, _e) => {
            // resetPopupModal(); // reset popup Modal (edit name, edit daily limit, edit monthly limit)

            if (isSelect) {
                setSelectedRows(rows);
            } else {
                setSelectedRows([]);
            }
        },
        onSelect: (row, isSelect, _rowIndex, _e) => {
            // resetPopupModal(); // reset popup Modal (edit name, edit daily limit, edit monthly limit)

            setSelectedRows((prevSelectedRows) => {
                const newSelectedRows = prevSelectedRows.filter((sRow) => (sRow.id !== row.id));
                if (isSelect) {
                    newSelectedRows.push(row);
                }
                return newSelectedRows;
            });
        },
    };

    const onBulkEdit = (selectedOption) => {
        if (selectedOption.value === ADMIN_STATUS.ACTIVE) {
            for (const row of selectedRows) {
                onUpdateStatus(row, ADMIN_STATUS.ACTIVE);
            }
            setSelectedRows([]);
        }

        if (selectedOption.value === ADMIN_STATUS.INACTIVE) {
            for (const row of selectedRows) {
                onUpdateStatus(row, ADMIN_STATUS.INACTIVE);
            }
            setSelectedRows([]);
        }

        if (selectedOption.value === 'delete') {
            for (const row of selectedRows) {
                onClickDeleteUserNotifications(row);
            }
            setSelectedRows([]);
        }
    };


    const fetchPage = (pageNumber = 1, append = false) => {
        if (admin_user_notifications_loading) return;
        appendModeRef.current = append;
        const queryParams = {};
        queryParams.page_number = pageNumber
        applyNotificationsTerminalSortParams(queryParams, sortState, backendSortingEnabled)
        setNotificationsPageLoading(true);
        dispatch(getAdminUserNotifications({ queryParams, useCache: shouldUseOffsetPagination }, true));
    }

    // A sort change resets to page 1 and refetches with the new sort.
    useEffect(() => {
        if (isInitialSortRender.current) {
            isInitialSortRender.current = false;
            return;
        }
        if (!backendSortingEnabled) return;
        fetchPage(1, false);
    }, [sortState]); // eslint-disable-line react-hooks/exhaustive-deps

    const [deleteNotificationModalState, setDeleteNotificationModalState] = useState({ action: false, row: null })

    useEffect(() => {
        if (!notificationsPageLoading || admin_user_notifications_loading) return;

        if (Number(last_page_fetched) > 0 || !appendModeRef.current) {
            if (appendModeRef.current) {
                setNotificationsList(prev => [...prev, ...(admin_user_notifications || [])])
            } else {
                setNotificationsList(admin_user_notifications || [])
            }
        }

        appendModeRef.current = false
        setNotificationsPageLoading(false)
    }, [admin_user_notifications, admin_user_notifications_loading, last_page_fetched, notificationsPageLoading])

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Notifications Terminal | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="" backOption={false} breadcrumbItem="Notifications Terminal" />
                    {/* Modals Start */}
                    {accountsOrUsersModalState?.active && <AccountsOrUsers
                        state={accountsOrUsersModalState}
                        onSubmitCreatefeaturesFlag={onConfirmStatusPrompt}
                        onCloseStateEvent={() => setAccountsOrUsersModalState({ active: false })}
                    />}
                    {/* Modals End */}
                    <DeleteModal
                        deleteModalState={deleteNotificationModalState}
                        onDeleteClick={(state) => {
                            onClickDeleteUserNotifications(state?.row);
                            onClickOpenDeleteNotificationModal(false);
                        }}
                        textMessage={USER_NOTIFICATION_DELETE_STR}
                        onCloseClick={() => onClickOpenDeleteNotificationModal(false)}
                        cancelButton
                    />
                    {/* Table starts */}

                    <Row>
                        <Col className="col-12">
                            <Card>
                                <CardBody>
                                    <ToolkitProvider
                                        keyField="id"
                                        columns={tableColumns}
                                        data={notificationsList}
                                        exportCSV={{
                                            fileName: "User_Notifications.csv",
                                            exportAll: false,
                                        }}
                                    >
                                        {(toolkitProps) => (
                                            <Row className="d-flex align-items-end">
                                                {selectedRows && selectedRows.length > 0
                                                    ? (

                                                        <Col xl="2" className="align-items-end">
                                                            <FormGroup className="mb-0">
                                                                <div>
                                                                    <Select
                                                                        placeholder={`Bulk Edit (${selectedRows.length})`}
                                                                        value={bulkEdit}
                                                                        onChange={onBulkEdit}
                                                                        options={optionBulkEdit}
                                                                        classNamePrefix="select2-selection"
                                                                    />
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                    ) : (
                                                        <Col xl="auto" className="ms-auto">
                                                            <Row>
                                                                <Col xl="auto">
                                                                    <div className="mb-0">
                                                                        <div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-sfblue"
                                                                                onClick={onClickExport}
                                                                                disabled={admin_user_notifications_loading}
                                                                            >
                                                                                <i className="bx bx-import font-size-14 align-middle me-2" />
                                                                                Export
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col xl="auto">
                                                                    <FormGroup className="mb-0">
                                                                        <div>
                                                                            <a
                                                                                className={`btn ${getPrimaryButtonClass()}`}
                                                                                onClick={onClickOpenModal}
                                                                            >
                                                                                <i className="bx bx-plus font-size-14 align-middle me-2" />
                                                                                New Notification
                                                                            </a>
                                                                        </div>
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>
                                                        </Col>)}

                                                <Col md={12} xl="12">
                                                    {notificationsPageLoading ? <SfLoadingSkeleton rows={8} cols={6} /> :
                                                        <div className="sfTable-Container">
                                                            <BootstrapTable
                                                                keyField="id"
                                                                key={"id"}
                                                                columns={tableColumns}
                                                                data={notificationsList}
                                                                responsive
                                                                bordered={false}
                                                                striped={false}
                                                                selectRow={selectRow}
                                                                defaultSorted={backendSortingEnabled ? undefined : DEFAULT_TABLE_SORT_BY_CREATED_AT_IN_DESC}
                                                                remote={backendSortingEnabled ? { pagination: false, filter: false, sort: true } : false}
                                                                onTableChange={handleTableChange}
                                                                rowClasses="tr-hover"
                                                                rowEvents={rowEvents}
                                                                headerClasses="sftableHeader text-black thead-h50"
                                                                classes="table align-middle table-nowrap text-black"
                                                                headerWrapperClasses="thead-light"
                                                                noDataIndication="You currently don't have any Notifications"
                                                                sort={{
                                                                    sortCaret: customSortCaret,
                                                                    ...(backendSortingEnabled && { dataField: sortState.column, order: sortState.direction }),
                                                                }}
                                                                {...toolkitProps.baseProps}
                                                            />
                                                        </div>}
                                                </Col>
                                                <Col md={12} xl="12">
                                                    {shouldUseOffsetPagination ? (
                                                        <Row>
                                                            <Col xl='12'>
                                                                <OffsetPagination
                                                                    currentPage={currentPage}
                                                                    totalPages={Math.max(Number(total_number_of_pages) || 0, Number(currentPage) || 1)}
                                                                    hasNextPage={currentPage < total_number_of_pages}
                                                                    hasDeepOffsetPages={false}
                                                                    loading={admin_user_notifications_loading}
                                                                    onPageChange={fetchPage}
                                                                    className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                                                />
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <Row>
                                                            {!admin_user_notifications_loading && Number(total_number_of_pages) > 0 ? (
                                                                <Col xl='auto'>
                                                                    {currentPage} out of {total_number_of_pages} page(s)
                                                                </Col>
                                                            ) : null}
                                                            <Col xl='auto' className='ms-auto'>
                                                                {currentPage < total_number_of_pages && (
                                                                    <button
                                                                        className='btn btn-outline-sfblue'
                                                                        onClick={() => fetchPage(currentPage + 1, true)}
                                                                    >
                                                                        {admin_user_notifications_loading ? 'Please wait...' : 'Load More'}
                                                                    </button>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    )}
                                                </Col>
                                            </Row>

                                        )}
                                    </ToolkitProvider>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Table ends */}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default NotificationsTerminal
